import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { logger } from '@core/utils';
import type { OrganizationSummary } from '@core/types/api';

/**
 * localStorage key holding the active organization uuid.
 *
 * The base query mirrors this key (`ACTIVE_ORGANIZATION_STORAGE_KEY`) as a
 * fallback when it has to build the `X-Organization` header before the store is
 * rehydrated — the two MUST stay in sync.
 */
export const ACTIVE_ORGANIZATION_KEY = 'activeOrganizationUuid';

export interface OrganizationState {
  activeOrganizationUuid: string | null;
  /**
   * Organization uuids the backend refused (403) during this session.
   *
   * Without this the self-heal is a guaranteed no-op: `baseQuery` clears the active
   * uuid, and `useActiveOrganization` immediately re-selects the same one out of the
   * persisted login payload — 403 → clear → re-select → 403, forever. Deliberately
   * NOT persisted: a rejection is about this session's server state, and a reload is
   * a fair moment to try again.
   */
  rejectedOrganizationUuids: string[];
}

/** Read the active organization uuid from localStorage, best effort. */
export const loadActiveOrganizationFromStorage = (): string | null => {
  try {
    return localStorage.getItem(ACTIVE_ORGANIZATION_KEY);
  } catch (error) {
    logger.error('Error loading active organization from localStorage:', error);
    return null;
  }
};

const persistActiveOrganization = (uuid: string): void => {
  try {
    localStorage.setItem(ACTIVE_ORGANIZATION_KEY, uuid);
  } catch (error) {
    logger.error('Error saving active organization to localStorage:', error);
  }
};

const clearPersistedActiveOrganization = (): void => {
  try {
    localStorage.removeItem(ACTIVE_ORGANIZATION_KEY);
  } catch (error) {
    logger.error('Error clearing active organization from localStorage:', error);
  }
};

/**
 * Pick the organization a session should open on.
 *
 * The account's `default_organization` wins when the caller still belongs to it,
 * then the first organization they are actually a member of (a superuser may see
 * organizations with a null role), then the first one listed. Returning the raw
 * default as a last resort covers a superuser whose list has not loaded yet.
 *
 * Any uuid in `rejected` is skipped — that is what stops a 403'd organization from
 * being re-selected on the very next render. When every candidate has been rejected
 * the answer is `null`, and the switcher says so instead of looping.
 *
 * Shared by the login flow and {@link useActiveOrganization} so both agree on
 * what "the active organization" means.
 */
export const preferredOrganizationUuid = (
  organizations: OrganizationSummary[],
  defaultUuid?: string | null,
  rejected: string[] = [],
): string | null => {
  const allowed = organizations.filter((organization) => !rejected.includes(organization.uuid));
  const fallback = defaultUuid && !rejected.includes(defaultUuid) ? defaultUuid : null;

  return (
    allowed.find((organization) => organization.uuid === defaultUuid)?.uuid ??
    allowed.find((organization) => organization.role != null)?.uuid ??
    allowed[0]?.uuid ??
    fallback
  );
};

/**
 * Organization slice — which tenant the user is currently acting on.
 *
 * Rehydrated at module load so the first request already carries the right
 * `X-Organization` header. Persistence lives in the reducer so mounting the
 * slice is all it takes for the selection to survive a reload.
 */
const organizationSlice = createSlice({
  name: 'organization',
  initialState: (): OrganizationState => ({
    activeOrganizationUuid: loadActiveOrganizationFromStorage(),
    rejectedOrganizationUuids: [],
  }),
  reducers: {
    initializeActiveOrganization: (state) => {
      state.activeOrganizationUuid = loadActiveOrganizationFromStorage();
    },
    setActiveOrganization: (state, action: PayloadAction<string>) => {
      state.activeOrganizationUuid = action.payload;
      state.rejectedOrganizationUuids = state.rejectedOrganizationUuids.filter(
        (uuid) => uuid !== action.payload,
      );
      persistActiveOrganization(action.payload);
    },
    /** Plain clear (user/tenant switch). Does NOT mark the uuid as rejected. */
    clearActiveOrganization: (state) => {
      state.activeOrganizationUuid = null;
      clearPersistedActiveOrganization();
    },
    /**
     * The backend refused the active organization (403). Clear it AND remember it,
     * so the next selection picks a different one instead of the same bad uuid.
     * Dispatched by `@core/api/baseQuery` by action type (core never imports a
     * feature), so it takes no payload — the uuid comes from state.
     */
    rejectActiveOrganization: (state) => {
      const rejected = state.activeOrganizationUuid;
      if (rejected && !state.rejectedOrganizationUuids.includes(rejected)) {
        state.rejectedOrganizationUuids.push(rejected);
      }
      state.activeOrganizationUuid = null;
      clearPersistedActiveOrganization();
    },
    /** Forget every rejection — a new session gets a clean slate. */
    resetRejectedOrganizations: (state) => {
      state.rejectedOrganizationUuids = [];
    },
  },
});

export const {
  initializeActiveOrganization,
  setActiveOrganization,
  clearActiveOrganization,
  rejectActiveOrganization,
  resetRejectedOrganizations,
} = organizationSlice.actions;

export default organizationSlice.reducer;

// Selectors. Typed loosely against the slice shape so they can be reused before
// RootState is constructed (avoids a circular import on the store).
interface WithOrganization {
  organization: OrganizationState;
}

export const selectActiveOrganizationUuid = (state: WithOrganization): string | null =>
  state.organization.activeOrganizationUuid;

export const selectRejectedOrganizationUuids = (state: WithOrganization): string[] =>
  state.organization.rejectedOrganizationUuids;
